import { useEffect, useState } from "react";
import api from "@/lib/api";
import PatientRoadmap from "@/components/PatientRoadmap";
import { Skeleton } from "@/components/Skeleton";
import { MapPin, Target, CalendarClock, Activity, Dumbbell, History } from "lucide-react";

const fmtDate = (s) => {
  try { return new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return s; }
};
const fmtDateTime = (s) => {
  try { return new Date(s).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return s; }
};

/**
 * RecoveryPlanPanel — Batch 8B · Shared Recovery Plan (patient, read-only).
 * Journey context shown above the Day / Week / Month exercise plan on My Plan:
 * selected Case + goal + next appointment + 7-day adherence + programme
 * summaries + the Case's recovery roadmap (deterministic link only). Pure
 * aggregation of existing data — all editing stays in its existing workflows.
 */
export default function RecoveryPlanPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selId, setSelId] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get("/my-plan/recovery");
      setData(res.data);
      setSelId((cur) => cur || res.data?.cases?.[0]?.id || null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load your recovery plan.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="fl-card fl-card-elevated p-4 mb-4" data-testid="recovery-plan-loading">
        <Skeleton className="h-4 w-40 rounded" />
        <Skeleton className="h-3 w-2/3 mt-3 rounded" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="fl-card p-4 mb-4 border" style={{ borderColor: "rgba(239,68,68,0.35)" }} data-testid="recovery-plan-error">
        <div className="fl-label text-red-300">Couldn't load your recovery plan</div>
        <p className="text-sm text-zinc-400 mt-1">{error}</p>
        <button onClick={load} className="fl-btn text-xs mt-3" data-testid="recovery-plan-retry">Retry</button>
      </div>
    );
  }

  const cases = data?.cases || [];
  if (cases.length === 0) return <RehabHistory />; // no active case → still surface discharge history

  const sel = cases.find((c) => c.id === selId) || cases[0];

  return (
    <div className="fl-card fl-card-elevated p-4 sm:p-5 mb-4" data-testid="recovery-plan">
      <div className="flex items-center gap-2">
        <MapPin size={14} className="text-[#f47a20]" />
        <div className="fl-label text-xs">Your recovery plan</div>
      </div>

      {cases.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="recovery-case-selector">
          {cases.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelId(c.id)}
              data-testid={`recovery-case-chip-${c.id}`}
              className={`px-3 py-1.5 rounded-full text-xs border transition ${
                sel.id === c.id
                  ? "border-[#f47a20] text-[#f47a20] bg-[#f47a20]/10"
                  : "border-white/10 text-zinc-400 hover:text-zinc-200"
              }`}
            >
              {c.title}
            </button>
          ))}
        </div>
      )}

      <div className="mt-3" data-testid={`recovery-case-${sel.id}`}>
        <h3 className="fl-heading text-lg sm:text-xl truncate" data-testid="recovery-case-title">{sel.title}</h3>
        {sel.body_region && <div className="text-xs text-zinc-500 mt-0.5">{sel.body_region}</div>}

        <div className="grid sm:grid-cols-3 gap-3 mt-4">
          <div className="rounded-lg border border-white/10 p-3" data-testid="recovery-goal">
            <div className="flex items-center gap-1.5 fl-label"><Target size={12} className="text-[#f47a20]" /> Goal</div>
            <div className="text-sm text-zinc-200 mt-1">{data.goal || "—"}</div>
          </div>
          <div className="rounded-lg border border-white/10 p-3" data-testid="recovery-next-appt">
            <div className="flex items-center gap-1.5 fl-label"><CalendarClock size={12} className="text-[#f47a20]" /> Next appointment</div>
            <div className="text-sm text-zinc-200 mt-1">
              {data.next_appointment ? fmtDateTime(data.next_appointment.scheduled_at) : "None booked"}
            </div>
            {data.next_appointment?.status && (
              <div className="text-[11px] text-zinc-500 mt-0.5 capitalize">{data.next_appointment.status}</div>
            )}
          </div>
          <div className="rounded-lg border border-white/10 p-3" data-testid="recovery-adherence">
            <div className="flex items-center gap-1.5 fl-label"><Activity size={12} className="text-[#f47a20]" /> 7-day adherence</div>
            <div className="text-sm text-zinc-200 mt-1">{data.adherence}%</div>
            <div className="h-1 bg-white/10 rounded mt-1 overflow-hidden"><div className="h-full bg-[#f47a20]" style={{ width: `${data.adherence}%` }} /></div>
          </div>
        </div>

        <div className="mt-4" data-testid="recovery-programmes">
          <div className="flex items-center gap-1.5 fl-label mb-2"><Dumbbell size={12} className="text-[#f47a20]" /> Programmes</div>
          {(sel.programmes || []).length === 0 ? (
            <div className="text-xs text-zinc-500">No active programme in this case yet.</div>
          ) : (
            <div className="space-y-1.5">
              {sel.programmes.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-3 text-sm border border-white/5 rounded-lg px-3 py-2" data-testid={`recovery-prog-${p.id}`}>
                  <div className="min-w-0">
                    <div className="text-zinc-200 truncate">{p.title}</div>
                    <div className="text-[11px] text-zinc-500">
                      {p.phase ? `${p.phase} · ` : ""}{p.exercise_count} exercise{p.exercise_count !== 1 ? "s" : ""}
                      {p.end_date ? ` · review by ${fmtDate(p.end_date)}` : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4" data-testid="recovery-roadmap-slot">
          {sel.roadmap?.roadmap ? (
            <PatientRoadmap roadmap={sel.roadmap.roadmap} history={sel.roadmap.history} />
          ) : (
            <div className="fl-card p-4" data-testid="recovery-roadmap-empty">
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-[#f47a20]" />
                <div className="fl-label text-xs">Recovery roadmap</div>
              </div>
              <p className="text-sm text-zinc-400 mt-2">No case-specific recovery roadmap has been assigned yet.</p>
            </div>
          )}
        </div>
      </div>

      <RehabHistory />
    </div>
  );
}

/* Batch 8D · patient-safe "Previous rehabilitation" — Closed Cases with a
 * clinician-written summary. Read-only; no internal closure metadata. */
const GOAL_FRIENDLY = {
  achieved: "Goal achieved",
  partially_achieved: "Goal partially achieved",
  not_achieved: "Goal not achieved",
  not_assessed: "Outcome not assessed",
  follow_up_declined: "Follow-up declined",
};

function RehabHistory() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get("/my-plan/rehabilitation-history")
      .then((r) => { if (alive) setItems(Array.isArray(r.data) ? r.data : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  if (items.length === 0) return null;
  return (
    <div className="mt-4 border-t border-white/5 pt-4" data-testid="rehab-history">
      <button onClick={() => setOpen((v) => !v)} className="flex items-center gap-2 text-xs text-zinc-400 hover:text-zinc-200" data-testid="rehab-history-toggle">
        <History size={13} className="text-[#f47a20]" /> Previous rehabilitation ({items.length})
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          {items.map((c) => (
            <div key={c.id} className="rounded-lg border border-white/10 p-3" data-testid={`rehab-history-${c.id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-sm text-zinc-200 font-medium">{c.title}</div>
                <div className="text-[11px] text-zinc-500">{c.closed_at ? new Date(c.closed_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}</div>
              </div>
              {c.goal_outcome && <div className="text-[11px] text-[#f8a766] mt-0.5">{GOAL_FRIENDLY[c.goal_outcome] || ""}</div>}
              {c.patient_summary && <p className="text-sm text-zinc-400 mt-1">{c.patient_summary}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
